import React from 'react'
import {
    Card,
    CardMedia,
    CardContent,
    Typography,
    Box,
    Chip,
    Button,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import useDialogModal from '../../hooks/useDialogModal'
import ProductDetail from '../productdetail'
import { config } from '../../utilities/constants'
import IncDec from '../ui/incdec'
import { useShoppingCart } from '../../context/ShoppingCartContext'
import { outageNote } from '../../data/stockOutages'
import { Colors } from '../../styles/theme'

// Styled components for enhanced visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',

    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 12px 24px rgba(0, 82, 71, 0.15)',
        borderColor: Colors.grayLight,

        '& .product-image': {
            transform: 'scale(1.05)',
        },
    },
}))

// `outOfStock` is a transient prop — stripped so it never reaches the DOM.
const StyledCardMedia = styled(CardMedia, {
    shouldForwardProp: (prop) => prop !== 'outOfStock',
})(({ outOfStock }) => ({
    paddingTop: '100%',
    position: 'relative',
    backgroundColor: Colors.cream,
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    marginTop: '1.25rem',
    // Muted image is the quiet visual cue that pairs with the badge.
    filter: outOfStock ? 'grayscale(0.7) opacity(0.75)' : 'none',
}))

const PriceBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5, 0),
    backgroundColor: 'rgba(130, 87, 53, 0.05)', // Light brown tint
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1, 0),
}))

const ProductTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    minHeight: '2.6em',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: theme.spacing(1),
}))

// Top-right: the existing 25-year warranty badge.
const WarrantyBadge = styled(Chip)({
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: Colors.brown,
    color: Colors.white,
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 24,
    zIndex: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
})

// Top-left: out-of-stock badge. Neutral grey rather than red — this is
// information, not an alarm.
const OutOfStockBadge = styled(Chip)({
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: Colors.grayDark,
    color: Colors.white,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    height: 24,
    zIndex: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
})

// Check if product is an ElectroBraid reel (has warranty)
const isElectroBraidReel = (product) => {
    const productID = product.productID?.toLowerCase() || ''
    // Only the three 1000 ft reels have the 25-year warranty
    return productID.includes('pbrc1000')
}

export default function ImprovedProductCard({ product }) {
    const {
        getItemQty,
        incrItemQty,
        decrItemQty,
        isCAD,
        isItemOutOfStock,
        outages,
    } = useShoppingCart()
    const curStr = isCAD ? ' CAD' : ' USD'
    const [ProductDetailDialog, showProductDetailDialog] =
        useDialogModal(ProductDetail)
    const qty = getItemQty(product.productIDNum)

    const handleIncrItemQty = () => incrItemQty(product.productIDNum)
    const handleDecrItemQty = () => decrItemQty(product.productIDNum)

    const showWarranty = isElectroBraidReel(product)
    const outOfStock = isItemOutOfStock(product.productIDNum)
    const note = outOfStock ? outageNote(outages, product.productIDNum) : ''

    return (
        <>
            <StyledCard elevation={2}>
                {outOfStock && (
                    <OutOfStockBadge label="OUT OF STOCK" size="small" />
                )}
                {showWarranty && <WarrantyBadge label="25-YR" size="small" />}

                {/* Product Image */}
                <StyledCardMedia
                    className="product-image"
                    outOfStock={outOfStock}
                    onClick={() => showProductDetailDialog()}
                    image={`${config.img_path}${product.href}.jpg`}
                    title={product.descriptionTitle}
                />

                {/* Product Content */}
                <CardContent sx={{ flexGrow: 1, px: 2, py: 2 }}>
                    <ProductTitle variant="h6">
                        {product.descriptionTitle}
                    </ProductTitle>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontSize: '0.8rem', mb: 1 }}
                    >
                        SKU: {product.productID}
                    </Typography>

                    {/* Price Display */}
                    <PriceBox>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: Colors.secondary }}
                        >
                            ${product.srp}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.85rem' }}
                        >
                            {curStr}
                        </Typography>
                    </PriceBox>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}
                    >
                        {product.unitLabel}
                    </Typography>
                </CardContent>

                {/* Actions — counter when in stock, notice when not */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                        pb: 2,
                        px: 2,
                    }}
                >
                    {outOfStock ? (
                        <Box
                            sx={{
                                width: '100%',
                                textAlign: 'center',
                                px: 1.5,
                                py: 1,
                                border: `1px solid rgba(185, 134, 71, 0.4)`,
                                backgroundColor: 'rgba(185, 134, 71, 0.08)',
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: Colors.secondary,
                                }}
                            >
                                Our Apologies. Temporarily out of stock
                            </Typography>
                            {note && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        color: Colors.grayDark,
                                        mt: 0.5,
                                    }}
                                >
                                    {note}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <IncDec
                            qty={qty}
                            onIncrItemQty={handleIncrItemQty}
                            onDecrItemQty={handleDecrItemQty}
                        />
                    )}

                    <Button
                        variant="text"
                        size="small"
                        onClick={() => showProductDetailDialog()}
                        sx={{
                            color: Colors.primary,
                            fontSize: '0.8rem',
                            textTransform: 'none',
                            minWidth: '44px',
                            minHeight: '44px',
                            '&:hover': {
                                color: Colors.boltGold,
                                backgroundColor: 'rgba(185, 134, 71, 0.08)',
                            },
                        }}
                    >
                        View Details
                    </Button>
                </Box>
            </StyledCard>

            <ProductDetailDialog product={product} />
        </>
    )
}
